import { Link, useParams, Navigate } from "react-router-dom";
import { Printer } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import CTASection from "../components/blocks/CTASection";
import { FRAMEWORKS } from "../data/frameworks";

const Section = ({ title, children, testId }) => (
  <section className="border-t border-platinum py-10" data-testid={testId}>
    <h2 className="font-sub text-lg font-semibold text-navy">{title}</h2>
    <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
    <div className="mt-5 max-w-3xl text-base leading-relaxed text-slate">{children}</div>
  </section>
);

export default function FrameworkPage() {
  const { slug } = useParams();
  const framework = FRAMEWORKS.find((f) => f.slug === slug);

  if (!framework) return <Navigate to="/thinking-toolbox" replace />;

  if (!framework.detailed) {
    return (
      <>
        <SEO
          title={`${framework.name} — Vantage Thinking Toolbox™`}
          description={`${framework.name}: ${framework.tagline} A proprietary framework of The Vantage Institute™. Full guide in preparation.`}
          path={`/thinking-toolbox/${framework.slug}`}
        />
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Thinking Toolbox™", to: "/thinking-toolbox" }, { label: framework.name }]} />
        <section className="container-x py-16 sm:py-24">
          <p className="eyebrow">Vantage Thinking Toolbox™</p>
          <h1 className="mt-4 font-display text-4xl font-semibold text-navy sm:text-5xl">{framework.name}</h1>
          <p className="mt-4 font-body text-lg italic text-gold-deep">{framework.tagline}</p>
          <div className="card mt-10 max-w-2xl border-gold" data-testid="framework-forthcoming">
            <p className="text-base leading-relaxed text-slate">
              The full guide for this framework — what it is, why it matters, when to use it, how it works, business
              application, example and exercise — is being finalised and will be published here.
            </p>
          </div>
          <Link to="/thinking-toolbox" className="text-link mt-10 inline-flex min-h-[44px] items-center" data-testid="framework-back">
            Back to the Thinking Toolbox™
          </Link>
        </section>
      </>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${framework.name} — Vantage Thinking Toolbox™`,
    description: framework.what,
    author: { "@type": "Organization", name: "The Vantage Institute™" },
    publisher: { "@type": "Organization", name: "The Vantage Institute™" },
    inLanguage: "en",
  };

  return (
    <>
      <SEO
        title={`${framework.name} — Vantage Thinking Toolbox™ | The Vantage Institute™`}
        description={framework.what.slice(0, 155)}
        path={`/thinking-toolbox/${framework.slug}`}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Thinking Toolbox™", to: "/thinking-toolbox" }, { label: framework.name }]} />

      <article className="container-x py-14 sm:py-20" data-testid="framework-page">
        <header className="max-w-3xl">
          <p className="eyebrow">Vantage Thinking Toolbox™</p>
          <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-navy sm:text-5xl">{framework.name}</h1>
          <p className="mt-4 font-body text-lg italic text-gold-deep">{framework.tagline}</p>
          <button
            type="button"
            onClick={() => window.print()}
            className="no-print mt-6 inline-flex min-h-[44px] items-center gap-2 border border-navy/30 px-5 font-sub text-xs font-semibold uppercase tracking-[0.12em] text-navy transition-colors hover:border-gold-deep hover:text-gold-deep"
            data-testid="framework-print"
            aria-label={`Print or save ${framework.name} as PDF`}
          >
            <Printer size={15} aria-hidden="true" />
            Print / Save as PDF
          </button>
        </header>

        <Section title="What it is" testId="framework-what"><p>{framework.what}</p></Section>
        <Section title="Why it matters" testId="framework-why"><p>{framework.why}</p></Section>
        <Section title="When to use it" testId="framework-when"><p>{framework.when}</p></Section>
        <Section title="How it works" testId="framework-how">
          <ol className="list-decimal space-y-2.5 pl-5">
            {framework.how.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </Section>
        <Section title="Business application" testId="framework-application"><p>{framework.application}</p></Section>
        <Section title="Example" testId="framework-example">
          <div className="border-l-2 border-gold bg-paper p-5 sm:p-6"><p>{framework.example}</p></div>
        </Section>
        <Section title="Exercise" testId="framework-exercise">
          <div className="border border-platinum bg-white p-5 sm:p-6">
            <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep">Practice</p>
            <p className="mt-3">{framework.exercise}</p>
          </div>
        </Section>
        <Section title="Related programmes" testId="framework-related">
          <ul className="space-y-2.5">
            {framework.related.map((r) => (
              <li key={r} className="flex items-center gap-3">
                <span className="h-px w-6 shrink-0 bg-gold" aria-hidden="true" />
                {r}
              </li>
            ))}
          </ul>
          <Link to="/programmes" className="text-link mt-6 inline-flex min-h-[44px] items-center" data-testid="framework-related-link">
            Explore the Programme Catalogue
          </Link>
        </Section>
      </article>

      <CTASection
        title={`Apply ${framework.name} to your business.`}
        text="Vantage programmes train leaders to use the frameworks on their own live situations — so thinking becomes capability."
        primaryLabel="Explore Programmes"
        primaryTo="/programmes"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="framework-cta"
      />
    </>
  );
}
